import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type HistoryEntryType =
  | 'item-update'
  | 'item-delete'
  | 'item-duplicate'
  | 'item-reorder'
  | 'bulk';

export interface HistoryEntry {
  id: string;
  estimateId: string;
  timestamp: number;
  type: HistoryEntryType;
  before: unknown;
  after: unknown;
}

export interface EstimateHistory {
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
}

export interface EstimateHistoryState {
  byEstimate: Record<string, EstimateHistory>;
}

const STORAGE_KEY = 'prostostroy.estimateHistory.v1';
const LIMIT = 50;

function loadInitialState(): EstimateHistoryState {
  if (typeof window === 'undefined') return { byEstimate: {} };
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { byEstimate: {} };
    const parsed = JSON.parse(raw) as EstimateHistoryState;
    if (!parsed || typeof parsed !== 'object' || !parsed.byEstimate) {
      return { byEstimate: {} };
    }
    return parsed;
  } catch {
    return { byEstimate: {} };
  }
}

function persist(state: EstimateHistoryState) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / serialization errors
  }
}

function ensureBucket(state: EstimateHistoryState, estimateId: string): EstimateHistory {
  let bucket = state.byEstimate[estimateId];
  if (!bucket) {
    bucket = { undoStack: [], redoStack: [] };
    state.byEstimate[estimateId] = bucket;
  }
  return bucket;
}

const initialState: EstimateHistoryState = loadInitialState();

const estimateHistorySlice = createSlice({
  name: 'estimateHistory',
  initialState,
  reducers: {
    pushHistory(state, action: PayloadAction<HistoryEntry>) {
      const bucket = ensureBucket(state, action.payload.estimateId);
      bucket.undoStack.push(action.payload);
      if (bucket.undoStack.length > LIMIT) bucket.undoStack.shift();
      bucket.redoStack = [];
      persist(state);
    },
    popUndo(state, action: PayloadAction<{ estimateId: string }>) {
      const bucket = ensureBucket(state, action.payload.estimateId);
      const entry = bucket.undoStack.pop();
      if (entry) bucket.redoStack.push(entry);
      persist(state);
    },
    popRedo(state, action: PayloadAction<{ estimateId: string }>) {
      const bucket = ensureBucket(state, action.payload.estimateId);
      const entry = bucket.redoStack.pop();
      if (entry) bucket.undoStack.push(entry);
      persist(state);
    },
    clearHistory(state, action: PayloadAction<{ estimateId?: string }>) {
      if (action.payload.estimateId) {
        delete state.byEstimate[action.payload.estimateId];
      } else {
        state.byEstimate = {};
      }
      persist(state);
    },
  },
});

export const { pushHistory, popUndo, popRedo, clearHistory } = estimateHistorySlice.actions;

export const selectUndoStack = (
  state: { estimateHistory: EstimateHistoryState },
  estimateId: string,
): HistoryEntry[] => state.estimateHistory.byEstimate[estimateId]?.undoStack ?? [];

export const selectRedoStack = (
  state: { estimateHistory: EstimateHistoryState },
  estimateId: string,
): HistoryEntry[] => state.estimateHistory.byEstimate[estimateId]?.redoStack ?? [];

export default estimateHistorySlice.reducer;
